import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { toast } from 'sonner';
import {
  UserPlus, Trash2, Loader2, Plus, Crown, Check, X, Key,
  UserCheck, UserX, ChevronDown, ChevronRight, Eye, AlertCircle, Shield,
} from 'lucide-react';

type PermissionType = 
  | 'access_editor' | 'view_templates' | 'edit_templates' | 'delete_templates'
  | 'manage_own_templates' | 'use_kiosk' | 'view_data_panel' | 'use_json_editor'
  | 'batch_import' | 'print_labels' | 'export_pdf' | 'use_data_carousel'
  | 'change_label_size' | 'use_presets' | 'change_settings' | 'use_clipboard'
  | 'manage_elements' | 'use_alignment' | 'view_preview' | 'export_json'
  | 'import_templates' | 'use_grid' | 'use_layers_panel' | 'edit_properties'
  | 'view_changelog' | 'use_zoom' | 'manage_categories' | 'use_field_undo'
  | 'duplicate_templates' | 'submit_templates'
  | 'view_dashboard' | 'use_keyboard_shortcuts' | 'view_help'
  | 'share_templates' | 'lock_templates' | 'version_templates'
  | 'use_snap_to_grid' | 'use_rulers' | 'use_multi_select' | 'resize_elements'
  | 'configure_print_settings' | 'bulk_print' | 'reprint_labels'
  | 'use_csv_import' | 'edit_field_data' | 'use_barcode_scanner' | 'use_mobile_scan'
  | 'view_shared_templates' | 'comment_templates'
  | 'view_own_activity' | 'view_print_history';

const ALL_PERMISSIONS: { value: PermissionType; label: string; category: string }[] = [
  { value: 'access_editor', label: 'Access Editor', category: 'Access' },
  { value: 'use_kiosk', label: 'Use Kiosk Mode', category: 'Access' },
  { value: 'change_settings', label: 'Change Settings', category: 'Access' },
  { value: 'view_changelog', label: 'View Changelog', category: 'Access' },
  { value: 'view_dashboard', label: 'View Dashboard', category: 'Access' },
  { value: 'use_keyboard_shortcuts', label: 'Keyboard Shortcuts', category: 'Access' },
  { value: 'view_help', label: 'View Help', category: 'Access' },
  { value: 'view_templates', label: 'View Templates', category: 'Templates' },
  { value: 'edit_templates', label: 'Edit Global Templates', category: 'Templates' },
  { value: 'delete_templates', label: 'Delete Templates', category: 'Templates' },
  { value: 'manage_own_templates', label: 'Manage Own Templates', category: 'Templates' },
  { value: 'use_presets', label: 'Use Presets', category: 'Templates' },
  { value: 'import_templates', label: 'Import Templates', category: 'Templates' },
  { value: 'manage_categories', label: 'Manage Categories', category: 'Templates' },
  { value: 'duplicate_templates', label: 'Duplicate Templates', category: 'Templates' },
  { value: 'submit_templates', label: 'Submit Templates', category: 'Templates' },
  { value: 'share_templates', label: 'Share Templates', category: 'Templates' },
  { value: 'lock_templates', label: 'Lock Templates', category: 'Templates' },
  { value: 'version_templates', label: 'Version History', category: 'Templates' },
  { value: 'manage_elements', label: 'Manage Elements', category: 'Editor' },
  { value: 'edit_properties', label: 'Edit Properties', category: 'Editor' },
  { value: 'use_clipboard', label: 'Use Clipboard', category: 'Editor' },
  { value: 'use_alignment', label: 'Use Alignment', category: 'Editor' },
  { value: 'change_label_size', label: 'Change Label Size', category: 'Editor' },
  { value: 'use_grid', label: 'Use Grid', category: 'Editor' },
  { value: 'use_layers_panel', label: 'Use Layers Panel', category: 'Editor' },
  { value: 'use_zoom', label: 'Use Zoom', category: 'Editor' },
  { value: 'use_snap_to_grid', label: 'Snap to Grid', category: 'Editor' },
  { value: 'use_rulers', label: 'Rulers', category: 'Editor' },
  { value: 'use_multi_select', label: 'Multi-Select', category: 'Editor' },
  { value: 'resize_elements', label: 'Resize Elements', category: 'Editor' },
  { value: 'print_labels', label: 'Print Labels', category: 'Printing' },
  { value: 'export_pdf', label: 'Export PDF', category: 'Printing' },
  { value: 'configure_print_settings', label: 'Print Settings', category: 'Printing' },
  { value: 'bulk_print', label: 'Bulk Print', category: 'Printing' },
  { value: 'reprint_labels', label: 'Reprint Labels', category: 'Printing' },
  { value: 'view_data_panel', label: 'View Data Panel', category: 'Data' },
  { value: 'batch_import', label: 'Batch Import', category: 'Data' },
  { value: 'use_data_carousel', label: 'Use Data Carousel', category: 'Data' },
  { value: 'use_json_editor', label: 'Use JSON Editor', category: 'Data' },
  { value: 'export_json', label: 'Export JSON', category: 'Data' },
  { value: 'use_field_undo', label: 'Use Field Undo/Redo', category: 'Data' },
  { value: 'use_csv_import', label: 'CSV Import', category: 'Data' },
  { value: 'edit_field_data', label: 'Edit Field Data', category: 'Data' },
  { value: 'use_barcode_scanner', label: 'Barcode Scanner', category: 'Data' },
  { value: 'use_mobile_scan', label: 'Mobile Scan', category: 'Data' },
  { value: 'view_preview', label: 'View Preview', category: 'Preview' },
  { value: 'view_shared_templates', label: 'View Shared', category: 'Collaboration' },
  { value: 'comment_templates', label: 'Comment Templates', category: 'Collaboration' },
  { value: 'view_own_activity', label: 'View Own Activity', category: 'Audit' },
  { value: 'view_print_history', label: 'View Print History', category: 'Audit' },
];

interface Group {
  id: string;
  name: string;
  description: string | null;
  permissions: PermissionType[];
}

export interface UserWithDetails {
  id: string;
  username: string;
  isAdmin: boolean;
  isApproved: boolean;
  mustChangePassword: boolean;
  groups: { id: string; name: string }[];
  effectivePermissions?: PermissionType[];
}

interface UserManagementProps {
  users: UserWithDetails[];
  groups: Group[];
  onRefreshUsers: () => void;
  onRefreshGroups: () => void;
}

export const UserManagement: React.FC<UserManagementProps> = ({
  users, groups, onRefreshUsers, onRefreshGroups,
}) => {
  const { user, startImpersonating } = useAuth();
  
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newUserIsAdmin, setNewUserIsAdmin] = useState(false);
  const [newUserGroups, setNewUserGroups] = useState<string[]>([]);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  
  const [assignGroupOpen, setAssignGroupOpen] = useState(false);
  const [assignGroupUserId, setAssignGroupUserId] = useState('');
  const [groupToAssign, setGroupToAssign] = useState('');
  
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [resetPasswordUserId, setResetPasswordUserId] = useState('');
  const [resetPasswordUsername, setResetPasswordUsername] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [forcePasswordChange, setForcePasswordChange] = useState(true);
  
  const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [bulkAssignGroupOpen, setBulkAssignGroupOpen] = useState(false);
  const [bulkGroupToAssign, setBulkGroupToAssign] = useState('');
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const handleCreateUser = async () => {
    if (!newUsername.trim() || !newPassword.trim()) { toast.error('Please enter username and password'); return; }
    setIsCreatingUser(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-users/create-user', {
        body: { username: newUsername.trim(), password: newPassword, autoApprove: true, isAdmin: newUserIsAdmin, groupIds: newUserGroups },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`User "${newUsername}" created`);
      setCreateUserOpen(false); setNewUsername(''); setNewPassword(''); setNewUserIsAdmin(false); setNewUserGroups([]);
      onRefreshUsers();
    } catch (error: any) { toast.error(error.message || 'Failed to create user'); }
    finally { setIsCreatingUser(false); }
  };

  const handleSuspendUser = async (userId: string, suspend: boolean) => {
    const { error } = await supabase.from('profiles').update({ is_approved: !suspend }).eq('user_id', userId);
    if (error) { toast.error('Failed to update'); return; }
    toast.success(suspend ? 'User suspended' : 'User reactivated');
    onRefreshUsers();
  };

  const handleResetPassword = async () => {
    if (!resetNewPassword.trim()) { toast.error('Enter a new password'); return; }
    setIsResettingPassword(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-users/reset-password', {
        body: { userId: resetPasswordUserId, newPassword: resetNewPassword, forcePasswordChange },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Password reset for "${resetPasswordUsername}"`);
      setResetPasswordOpen(false); setResetNewPassword(''); setForcePasswordChange(true);
    } catch (error: any) { toast.error(error.message || 'Failed'); }
    finally { setIsResettingPassword(false); }
  };

  const handleDeleteUser = async (userId: string, username: string) => {
    if (!confirm(`Delete user "${username}"? This cannot be undone.`)) return;
    try {
      const { data, error } = await supabase.functions.invoke('admin-users/delete-user', { body: { userId } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`User "${username}" deleted`);
      onRefreshUsers();
    } catch (error: any) { toast.error(error.message || 'Failed'); }
  };

  const handleToggleAdmin = async (userId: string, makeAdmin: boolean) => {
    if (makeAdmin) {
      const { error } = await supabase.from('user_roles').insert({ user_id: userId, role: 'admin' });
      if (error) { toast.error('Failed'); return; }
    } else {
      const { error } = await supabase.from('user_roles').delete().eq('user_id', userId).eq('role', 'admin');
      if (error) { toast.error('Failed'); return; }
    }
    toast.success(makeAdmin ? 'Admin role granted' : 'Admin role revoked');
    onRefreshUsers();
  };

  const handleAssignGroup = async (userId: string) => {
    if (!groupToAssign) return;
    const { error } = await supabase.from('user_groups').insert({ user_id: userId, group_id: groupToAssign });
    if (error) { toast.error(error.message.includes('duplicate') ? 'Already in group' : 'Failed'); return; }
    toast.success('Group assigned');
    setAssignGroupOpen(false); setGroupToAssign('');
    onRefreshUsers();
  };

  const handleRemoveFromGroup = async (userId: string, groupId: string) => {
    const { error } = await supabase.from('user_groups').delete().eq('user_id', userId).eq('group_id', groupId);
    if (error) { toast.error('Failed'); return; }
    toast.success('Removed from group');
    onRefreshUsers();
  };

  const handleBulkApprove = async (approve: boolean) => {
    setBulkProcessing(true);
    let n = 0;
    for (const userId of selectedUsers) {
      const { error } = await supabase.from('profiles').update({ is_approved: approve }).eq('user_id', userId);
      if (!error) n++;
    }
    setBulkProcessing(false);
    toast.success(`${approve ? 'Approved' : 'Suspended'} ${n} user(s)`);
    setSelectedUsers(new Set());
    onRefreshUsers();
  };

  const handleBulkForcePasswordChange = async () => {
    setBulkProcessing(true);
    let n = 0;
    for (const userId of selectedUsers) {
      const { error } = await supabase.from('profiles').update({ must_change_password: true }).eq('user_id', userId);
      if (!error) n++;
    }
    setBulkProcessing(false);
    toast.success(`Forced password change for ${n} user(s)`);
    setSelectedUsers(new Set());
    onRefreshUsers();
  };

  const handleBulkAssignGroup = async () => {
    if (!bulkGroupToAssign) return;
    setBulkProcessing(true);
    let n = 0;
    for (const userId of selectedUsers) {
      const { error } = await supabase.from('user_groups').insert({ user_id: userId, group_id: bulkGroupToAssign });
      if (!error) n++;
    }
    setBulkProcessing(false);
    toast.success(`Assigned group to ${n} user(s)`);
    setBulkAssignGroupOpen(false); setBulkGroupToAssign('');
    setSelectedUsers(new Set());
    onRefreshUsers();
  };

  const toggleUserSelected = (userId: string) => {
    const s = new Set(selectedUsers);
    s.has(userId) ? s.delete(userId) : s.add(userId);
    setSelectedUsers(s);
  };

  const toggleUserExpanded = (userId: string) => {
    const s = new Set(expandedUsers);
    s.has(userId) ? s.delete(userId) : s.add(userId);
    setExpandedUsers(s);
  };

  const pwValid = (pw: string) =>
    pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw) && /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(pw);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Users</h2>
          <p className="text-sm text-muted-foreground">Manage user accounts, roles, and approvals</p>
        </div>
        <Button onClick={() => setCreateUserOpen(true)} className="gap-2">
          <UserPlus className="w-4 h-4" />
          Create User
        </Button>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border bg-muted/30"><div className="text-2xl font-bold">{users.length}</div><div className="text-xs text-muted-foreground">Total</div></div>
        <div className="p-3 rounded-lg border bg-muted/30"><div className="text-2xl font-bold">{users.filter(u => u.isAdmin).length}</div><div className="text-xs text-muted-foreground">Admins</div></div>
        <div className="p-3 rounded-lg border bg-muted/30"><div className="text-2xl font-bold text-success">{users.filter(u => u.isApproved).length}</div><div className="text-xs text-muted-foreground">Active</div></div>
        <div className="p-3 rounded-lg border bg-muted/30"><div className="text-2xl font-bold text-warning">{users.filter(u => !u.isApproved).length}</div><div className="text-xs text-muted-foreground">Pending</div></div>
      </div>

      {/* Bulk actions */}
      {selectedUsers.size > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-lg border bg-primary/5 border-primary/20">
          <span className="text-sm font-medium">{selectedUsers.size} selected</span>
          <div className="flex items-center gap-2 ml-auto flex-wrap">
            <Button variant="outline" size="sm" onClick={() => handleBulkApprove(true)} disabled={bulkProcessing}><UserCheck className="w-4 h-4 mr-1" />Approve</Button>
            <Button variant="outline" size="sm" onClick={() => handleBulkApprove(false)} disabled={bulkProcessing}><UserX className="w-4 h-4 mr-1" />Suspend</Button>
            <Button variant="outline" size="sm" onClick={() => setBulkAssignGroupOpen(true)} disabled={bulkProcessing}><Shield className="w-4 h-4 mr-1" />Assign Group</Button>
            <Button variant="outline" size="sm" onClick={handleBulkForcePasswordChange} disabled={bulkProcessing}><Key className="w-4 h-4 mr-1" />Force PW Change</Button>
            <Button variant="ghost" size="sm" onClick={() => setSelectedUsers(new Set())}><X className="w-4 h-4" /></Button>
          </div>
        </div>
      )}

      {/* Select all */}
      <div className="flex items-center gap-2">
        <Checkbox
          checked={selectedUsers.size === users.length && users.length > 0}
          onCheckedChange={() => {
            if (selectedUsers.size === users.length) setSelectedUsers(new Set());
            else setSelectedUsers(new Set(users.map(u => u.id)));
          }}
        />
        <span className="text-sm text-muted-foreground">Select all</span>
      </div>

      {/* User list */}
      <ScrollArea className="h-[600px]">
        <div className="space-y-2">
          {users.map((u) => (
            <Collapsible key={u.id} open={expandedUsers.has(u.id)} onOpenChange={() => toggleUserExpanded(u.id)}>
              <div className="rounded-lg border bg-card">
                <CollapsibleTrigger asChild>
                  <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={selectedUsers.has(u.id)}
                          onCheckedChange={() => toggleUserSelected(u.id)}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                        {expandedUsers.has(u.id) ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium">{u.username}</span>
                          {u.isAdmin && <Badge className="bg-primary/10 text-primary text-[10px]"><Crown className="w-3 h-3 mr-0.5" />Admin</Badge>}
                          {u.isApproved
                            ? <Badge variant="secondary" className="bg-success/10 text-success text-[10px]"><Check className="w-3 h-3 mr-0.5" />Active</Badge>
                            : <Badge variant="secondary" className="bg-warning/10 text-warning text-[10px]"><AlertCircle className="w-3 h-3 mr-0.5" />Pending</Badge>
                          }
                          {u.mustChangePassword && <Badge variant="secondary" className="bg-destructive/10 text-destructive text-[10px]"><Key className="w-3 h-3 mr-0.5" />PW Change</Badge>}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {u.groups.slice(0, 3).map(g => <Badge key={g.id} variant="outline" className="text-[10px]">{g.name}</Badge>)}
                          {u.groups.length > 3 && <Badge variant="outline" className="text-[10px]">+{u.groups.length - 3}</Badge>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.isApproved ? (
                        <Button variant="outline" size="sm" className="gap-1 text-warning h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleSuspendUser(u.id, true); }} disabled={u.id === user?.id}>
                          <UserX className="w-3.5 h-3.5" />Suspend
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="gap-1 text-success h-7 text-xs" onClick={(e) => { e.stopPropagation(); handleSuspendUser(u.id, false); }}>
                          <UserCheck className="w-3.5 h-3.5" />Activate
                        </Button>
                      )}
                      <Badge variant="secondary" className="text-[10px]">{u.effectivePermissions?.length || 0} perms</Badge>
                    </div>
                  </div>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <Separator />
                  <div className="p-4 space-y-4 bg-muted/20">
                    {/* Groups */}
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">Groups</Label>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {u.groups.map(g => (
                          <Badge key={g.id} variant="secondary" className="cursor-pointer hover:bg-destructive/20" onClick={() => handleRemoveFromGroup(u.id, g.id)}>
                            {g.name}<X className="w-3 h-3 ml-1" />
                          </Badge>
                        ))}
                        <Button variant="outline" size="sm" className="h-6 text-xs" onClick={() => { setAssignGroupUserId(u.id); setAssignGroupOpen(true); }}>
                          <Plus className="w-3 h-3 mr-1" />Add Group
                        </Button>
                      </div>
                    </div>

                    {/* Permissions grid */}
                    <div>
                      <Label className="text-xs text-muted-foreground uppercase tracking-wide">
                        Effective Permissions {u.isAdmin && <span className="text-primary ml-1">(Admin - Full Access)</span>}
                      </Label>
                      <div className="grid grid-cols-3 lg:grid-cols-4 gap-1.5 mt-2">
                        {ALL_PERMISSIONS.map(perm => (
                          <div key={perm.value} className={`text-[10px] p-1.5 rounded border ${u.effectivePermissions?.includes(perm.value) ? 'bg-success/10 border-success/30 text-success' : 'bg-muted/30 border-muted text-muted-foreground'}`}>
                            <div className="flex items-center gap-1">
                              {u.effectivePermissions?.includes(perm.value) ? <Check className="w-2.5 h-2.5" /> : <X className="w-2.5 h-2.5" />}
                              {perm.label}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Force PW change */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={u.mustChangePassword}
                        onCheckedChange={async (checked) => {
                          const { error } = await supabase.from('profiles').update({ must_change_password: checked }).eq('user_id', u.id);
                          if (error) toast.error('Failed');
                          else { toast.success(checked ? 'Must change password' : 'Requirement removed'); onRefreshUsers(); }
                        }}
                        disabled={u.id === user?.id}
                      />
                      <Label className="text-sm">Force password change on next login</Label>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-wrap pt-2">
                      <Button variant="outline" size="sm" onClick={() => { setResetPasswordUserId(u.id); setResetPasswordUsername(u.username); setResetPasswordOpen(true); }}>
                        <Key className="w-4 h-4 mr-1" />Reset Password
                      </Button>
                      <Button variant={u.isAdmin ? 'destructive' : 'outline'} size="sm" onClick={() => handleToggleAdmin(u.id, !u.isAdmin)} disabled={u.id === user?.id}>
                        <Crown className="w-4 h-4 mr-1" />{u.isAdmin ? 'Remove Admin' : 'Make Admin'}
                      </Button>
                      {!u.isAdmin && u.id !== user?.id && (
                        <Button variant="outline" size="sm" onClick={() => { startImpersonating(u.username, (u.effectivePermissions || []) as any); toast.success(`Viewing as ${u.username}`); }}>
                          <Eye className="w-4 h-4 mr-1" />View As
                        </Button>
                      )}
                      <div className="flex-1" />
                      <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDeleteUser(u.id, u.username)} disabled={u.id === user?.id}>
                        <Trash2 className="w-4 h-4 mr-1" />Delete
                      </Button>
                    </div>
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          ))}
          {users.length === 0 && <div className="text-center py-8 text-muted-foreground">No users found.</div>}
        </div>
      </ScrollArea>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Create a new user account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input placeholder="john.doe" value={newUsername} onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_.]/g, ''))} />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" placeholder="••••••••" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              <ul className="text-xs text-muted-foreground space-y-0.5 mt-1">
                <li className={newPassword.length >= 8 ? 'text-primary' : ''}>• At least 8 characters</li>
                <li className={/[A-Z]/.test(newPassword) ? 'text-primary' : ''}>• One uppercase letter</li>
                <li className={/[a-z]/.test(newPassword) ? 'text-primary' : ''}>• One lowercase letter</li>
                <li className={/[0-9]/.test(newPassword) ? 'text-primary' : ''}>• One number</li>
                <li className={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(newPassword) ? 'text-primary' : ''}>• One special character</li>
              </ul>
            </div>
            <div className="flex items-center space-x-2">
              <Switch checked={newUserIsAdmin} onCheckedChange={setNewUserIsAdmin} />
              <Label className="flex items-center gap-2"><Crown className="w-4 h-4 text-primary" />Administrator</Label>
            </div>
            {!newUserIsAdmin && groups.length > 0 && (
              <div className="space-y-2">
                <Label>Assign to Groups</Label>
                <div className="grid grid-cols-2 gap-2 max-h-32 overflow-y-auto p-2 border rounded-md">
                  {groups.map(g => (
                    <div key={g.id} className="flex items-center space-x-2">
                      <Checkbox checked={newUserGroups.includes(g.id)} onCheckedChange={(c) => c ? setNewUserGroups([...newUserGroups, g.id]) : setNewUserGroups(newUserGroups.filter(x => x !== g.id))} />
                      <label className="text-sm">{g.name}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={isCreatingUser || !newUsername || !pwValid(newPassword)}>
              {isCreatingUser ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Create User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Group Dialog */}
      <Dialog open={assignGroupOpen} onOpenChange={setAssignGroupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign to Group</DialogTitle></DialogHeader>
          <Select value={groupToAssign} onValueChange={setGroupToAssign}>
            <SelectTrigger><SelectValue placeholder="Select a group" /></SelectTrigger>
            <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignGroupOpen(false)}>Cancel</Button>
            <Button onClick={() => handleAssignGroup(assignGroupUserId)} disabled={!groupToAssign}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetPasswordOpen} onOpenChange={setResetPasswordOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reset Password</DialogTitle><DialogDescription>Set a new password for {resetPasswordUsername}</DialogDescription></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input type="password" value={resetNewPassword} onChange={(e) => setResetNewPassword(e.target.value)} />
              <ul className="text-xs text-muted-foreground space-y-0.5">
                <li className={resetNewPassword.length >= 8 ? 'text-primary' : ''}>• At least 8 characters</li>
                <li className={/[A-Z]/.test(resetNewPassword) ? 'text-primary' : ''}>• One uppercase</li>
                <li className={/[a-z]/.test(resetNewPassword) ? 'text-primary' : ''}>• One lowercase</li>
                <li className={/[0-9]/.test(resetNewPassword) ? 'text-primary' : ''}>• One number</li>
                <li className={/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(resetNewPassword) ? 'text-primary' : ''}>• One special character</li>
              </ul>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox checked={forcePasswordChange} onCheckedChange={(c) => setForcePasswordChange(c === true)} />
              <label className="text-sm">Require change on next login</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetPasswordOpen(false)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={isResettingPassword || !pwValid(resetNewPassword)}>
              {isResettingPassword ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignGroupOpen} onOpenChange={setBulkAssignGroupOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Bulk Assign Group</DialogTitle><DialogDescription>Assign {selectedUsers.size} user(s) to a group</DialogDescription></DialogHeader>
          <Select value={bulkGroupToAssign} onValueChange={setBulkGroupToAssign}>
            <SelectTrigger><SelectValue placeholder="Select a group" /></SelectTrigger>
            <SelectContent>{groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}</SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkAssignGroupOpen(false)}>Cancel</Button>
            <Button onClick={handleBulkAssignGroup} disabled={!bulkGroupToAssign || bulkProcessing}>
              {bulkProcessing && <Loader2 className="w-4 h-4 animate-spin mr-2" />}Assign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
